import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { MaskLine, easeOut, scrollToId } from "./shared";
import { useLang } from "@/i18n";

const statValues = ["04", "12", "100%", "01"];

export default function Hero() {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [5, -5]), { stiffness: 120, damping: 18 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-5, 5]), { stiffness: 120, damping: 18 });

  const onMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  const line2 = t("hero.line2");
  const stats = t("hero.stats");

  return (
    <section
      id="top"
      ref={ref}
      onMouseMove={onMove}
      className="relative flex min-h-screen flex-col justify-between overflow-hidden pt-28"
      data-testid="hero-section"
    >
      <div className="pointer-events-none absolute -right-24 top-1/3 hidden select-none font-serif text-[22rem] font-light leading-none text-outline lg:block" aria-hidden="true">
        अ
      </div>

      <div className="mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-12 px-6 lg:grid-cols-12 lg:px-12">
        <div className="lg:col-span-7">
          <MaskLine delay={0.1}>
            <span className="text-xs font-semibold uppercase tracking-[0.3em] text-terracotta">
              {t("hero.overline")}
            </span>
          </MaskLine>

          <h1 className="mt-6 font-serif font-medium uppercase leading-[0.95] tracking-tight">
            <MaskLine delay={0.25} className="text-5xl sm:text-6xl lg:text-7xl">
              <span>{t("hero.line1")}</span>
            </MaskLine>
            <MaskLine delay={0.4} className="text-5xl sm:text-6xl lg:text-7xl">
              <span>
                {line2.replace(/\.$/, "")}<span className="text-ochre">.</span>
              </span>
            </MaskLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7, ease: easeOut }}
            className="mt-8 max-w-md text-base font-light leading-relaxed text-moss sm:text-lg"
            data-testid="hero-tagline"
          >
            {t("hero.tagline")}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9, ease: easeOut }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              onClick={() => scrollToId("#oils")}
              data-testid="hero-explore-oils-button"
              className="rounded-full bg-ink px-7 py-3.5 text-xs font-semibold uppercase tracking-[0.2em] text-cream transition-colors duration-300 hover:bg-terracotta"
            >
              {t("hero.ctaOils")}
            </button>
            <button
              onClick={() => scrollToId("#story")}
              data-testid="hero-our-story-button"
              className="rounded-full border border-ink/25 px-7 py-3.5 text-xs font-semibold uppercase tracking-[0.2em] text-ink transition-colors duration-300 hover:border-ink hover:bg-ink hover:text-cream"
            >
              {t("hero.ctaStory")}
            </button>
          </motion.div>
        </div>

        <div className="relative lg:col-span-5" style={{ perspective: 900 }}>
          <motion.div
            initial={{ clipPath: "inset(100% 0% 0% 0%)" }}
            animate={{ clipPath: "inset(0% 0% 0% 0%)" }}
            transition={{ duration: 1.2, delay: 0.5, ease: easeOut }}
            style={{ rotateX, rotateY }}
            className="arch-frame relative mx-auto aspect-[3/4] w-full max-w-sm overflow-hidden border border-ink/10"
          >
            <motion.img
              src="/assets/farm/hero-seeds-pour.jpeg"
              alt="A Sira Shakti producer pouring mustard seeds into the press"
              style={{ y: imgY, scale: 1.25 }}
              className="h-full w-full object-cover"
            />
          </motion.div>
          <motion.img
            src="/assets/logo.jpeg"
            alt="Arniveda — Gaon se Ghar tak"
            initial={{ opacity: 0, scale: 0.6, rotate: -12 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ duration: 0.9, delay: 1.1, ease: easeOut }}
            className="absolute -bottom-8 -left-4 h-28 w-28 rounded-full bg-cream object-cover p-1 shadow-xl ring-1 ring-ink/10 sm:-left-10 sm:h-36 sm:w-36"
            data-testid="hero-logo-badge"
          />
        </div>
      </div>

      <div className="mx-auto mt-16 w-full max-w-7xl px-6 pb-10 lg:px-12">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.3 }}
          className="flex flex-wrap items-end justify-between gap-8 border-t border-ink/10 pt-8"
        >
          <div className="grid flex-1 grid-cols-2 gap-6 sm:grid-cols-4" data-testid="hero-stats">
            {statValues.map((value, i) => (
              <div key={value}>
                <p className="font-serif text-3xl font-medium sm:text-4xl">{value}</p>
                <p className="mt-1 text-xs uppercase tracking-[0.18em] text-moss">{stats[i]}</p>
              </div>
            ))}
          </div>
          <button
            onClick={() => scrollToId("#story")}
            aria-label="Scroll to story"
            data-testid="hero-scroll-down-button"
            className="hidden h-12 w-12 items-center justify-center rounded-full border border-ink/20 transition-colors hover:bg-ink hover:text-cream sm:flex"
          >
            <ArrowDown className="h-4 w-4 animate-bounce" />
          </button>
        </motion.div>
      </div>
    </section>
  );
}
